import { NavLink } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AppSidebarIcon } from '@/layouts/AppSidebarIcons';
import type { AppNavGroup } from '@/shared/config/appNav';
import { routes } from '@/shared/config/routes';
import { prefetchScreensList } from '@/features/billboards/lib/prefetchScreensList';
import { useNotificationUnreadCount } from '@/features/notification/api/useNotificationQueries';
import { UserAvatar } from '@/shared/ui/UserAvatar';

type AppSidebarProps = {
  groups: AppNavGroup[];
  displayName: string;
  roleLabel: string;
  initial: string;
  avatarUrl?: string | null;
  city?: string | null;
  onCloseMobile: () => void;
  onLogout: () => void;
  isLoggingOut?: boolean;
};

export function AppSidebar({
  groups,
  displayName,
  roleLabel,
  initial,
  avatarUrl,
  city,
  onCloseMobile,
  onLogout,
  isLoggingOut = false,
}: AppSidebarProps) {
  const { data: unreadCount = 0 } = useNotificationUnreadCount();

  return (
    <aside className="dash-sidebar flex h-full w-[248px] flex-col">
      <div className="flex items-center justify-between px-5 pt-5 pb-4">
        <NavLink to="/dashboard" className="inline-flex items-center">
          <img
            src="/screenista-logo.svg"
            alt="Screenista"
            className="h-8 w-auto max-w-[148px] object-contain"
          />
        </NavLink>
        <button
          type="button"
          className="rounded-lg border-none bg-transparent p-2 text-[var(--dash-muted)] lg:hidden"
          onClick={onCloseMobile}
          aria-label="Close menu"
        >
          ✕
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {groups.map((group) => (
          <div key={group.title || 'main'} className="mb-4">
            {group.title ? (
              <p className="mb-1.5 px-3.5 text-[11px] font-extrabold tracking-[1px] text-[#6A6688] uppercase">
                {group.title}
              </p>
            ) : null}
            <div className="flex flex-col gap-1">
              {group.items.map((item) => {
                const showUnread =
                  item.to === routes.notifications && unreadCount > 0;

                return (
                  <NavLink
                    key={item.to + item.label}
                    to={item.to}
                    onClick={onCloseMobile}
                    onMouseEnter={
                      item.to === '/screens'
                        ? () => void prefetchScreensList(city)
                        : undefined
                    }
                    onFocus={
                      item.to === '/screens'
                        ? () => void prefetchScreensList(city)
                        : undefined
                    }
                    className={({ isActive }) =>
                      `flex h-[42px] items-center gap-3 rounded-[14px] px-3.5 text-[14px] transition-colors ${
                        isActive
                          ? 'dash-nav-active font-extrabold shadow-sm'
                          : 'font-semibold text-[#4A4670] hover:bg-[#F3F2F9] hover:text-[#3A3660]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <AppSidebarIcon name={item.icon} active={isActive} />
                        <span className="min-w-0 flex-1 truncate">
                          {item.label}
                        </span>
                        {showUnread ? (
                          <span
                            className="flex h-[20px] min-w-[20px] shrink-0 items-center justify-center rounded-full bg-[#E5384F] px-2 text-[11px] font-extrabold text-white"
                            aria-label={`${unreadCount} unread notifications`}
                          >
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        ) : null}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-[#F1EFF9] p-3">
        <NavLink
          to={routes.profile}
          onClick={onCloseMobile}
          className="mb-2.5 flex items-center gap-2.5 rounded-[16px] bg-[#F3F2F9] p-2.5 transition hover:brightness-[0.98]"
        >
          <UserAvatar
            url={avatarUrl}
            initial={initial}
            className="h-10 w-10 text-[13px] font-extrabold"
            alt={displayName}
          />
          <div className="min-w-0 flex-1 text-left">
            <p className="m-0 truncate text-[14px] font-extrabold text-[#3A3660]">
              {displayName}
            </p>
            <p className="m-0 truncate text-[12px] text-[#6A6688]">
              {roleLabel}
            </p>
          </div>
        </NavLink>
        <button
          type="button"
          onClick={onLogout}
          disabled={isLoggingOut}
          aria-busy={isLoggingOut}
          className="inline-flex h-[42px] w-full items-center justify-center gap-2 rounded-[14px] border border-[#F3C4CD] bg-[#FFF5F6] px-3 text-[14px] font-bold text-[#B4233A] transition hover:bg-[#FFEAEF] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isLoggingOut ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Signing out…
            </>
          ) : (
            'Logout'
          )}
        </button>
      </div>
    </aside>
  );
}
